import { AsYouType, parsePhoneNumberFromString, type CountryCode, type PhoneNumber } from 'libphonenumber-js';

const DEFAULT_COUNTRY: CountryCode = 'US';

/** US numbers, plus ambiguous +1 NANP (e.g. 555) with no country assigned. Rejects CA and other non-US. */
const isUsPhoneNumber = (parsed: PhoneNumber): boolean => parsed.country === 'US' || (parsed.countryCallingCode === '1' && !parsed.country);

/**
 * Normalize raw / legacy phone input to an internal US-only representation.
 * Complete US numbers → E.164 (`+15551234567`). Incomplete input keeps national
 * (or `+1` drafts) digits so the display edge can format as-you-type.
 * Non-US numbers are rejected.
 */
export function toE164(raw: string, defaultCountry: CountryCode = DEFAULT_COUNTRY): string {
    const trimmed = raw.trim();
    if (!trimmed) {
        return '';
    }

    const asYouType = new AsYouType(defaultCountry);
    asYouType.input(trimmed);
    const fromFormatter = asYouType.getNumber();
    if (fromFormatter?.isPossible()) {
        return isUsPhoneNumber(fromFormatter) ? fromFormatter.format('E.164') : '';
    }

    const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
    if (parsed?.isPossible()) {
        return isUsPhoneNumber(parsed) ? parsed.format('E.164') : '';
    }

    if (trimmed.startsWith('+')) {
        const digits = trimmed.replace(/\D/g, '');
        if (!digits) {
            return '';
        }
        // Only allow US country code (+1) while typing; reject other country codes.
        if (digits === '1') {
            return '+1';
        }
        if (digits.startsWith('1')) {
            return `+1${digits.slice(1, 11)}`;
        }
        return '';
    }

    return asYouType.getNationalNumber() || trimmed.replace(/\D/g, '').slice(0, 10);
}

/**
 * Emit E.164 only for complete/possible US numbers (API / persistence).
 * Incomplete or non-US values become `''` so callers can omit them.
 */
export function toE164ForStorage(raw: string, defaultCountry: CountryCode = DEFAULT_COUNTRY): string {
    const trimmed = raw.trim();
    if (!trimmed) {
        return '';
    }
    const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
    if (parsed?.isPossible() && isUsPhoneNumber(parsed)) {
        return parsed.format('E.164');
    }
    return '';
}

/**
 * Optional phone fields: empty is valid; non-empty must be a complete US number.
 */
export function isOptionalUsPhoneValid(raw: string, defaultCountry: CountryCode = DEFAULT_COUNTRY): boolean {
    const trimmed = raw.trim();
    if (!trimmed) {
        return true;
    }
    return Boolean(toE164ForStorage(trimmed, defaultCountry));
}

/**
 * Format an internal value (E.164 or partial digits) for display at the UI edge.
 * Non-US numbers are not shown.
 */
export function formatPhoneDisplay(stored: string, defaultCountry: CountryCode = DEFAULT_COUNTRY): string {
    if (!stored) {
        return '';
    }

    const parsed = parsePhoneNumberFromString(stored, defaultCountry);
    if (parsed?.isPossible()) {
        return isUsPhoneNumber(parsed) ? parsed.formatNational() : '';
    }

    if (stored.startsWith('+')) {
        const digits = stored.replace(/\D/g, '');
        if (digits && !digits.startsWith('1')) {
            return '';
        }
        return new AsYouType(defaultCountry).input(stored);
    }

    return new AsYouType(defaultCountry).input(stored);
}

/**
 * Input pipeline: raw keystrokes/paste → internal E.164 (or partial US digits).
 */
export function formatUsPhoneInput(raw: string, defaultCountry: CountryCode = DEFAULT_COUNTRY): string {
    return toE164(raw, defaultCountry);
}
